import { useEffect, useState } from "react";
import {
  fetchAdminCategories,
  createAdminCategory,
  updateAdminCategory,
  deleteAdminCategory,
  swapCategorySortOrder
} from "../../services/adminService";

function Categories() {
  const [list, setList]         = useState([]);
  const [adding, setAdding]     = useState(false);
  const [newName, setNewName]   = useState("");
  const [addError, setAddError] = useState("");
  const [editing, setEditing]   = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [reordering, setReordering] = useState(false); // prevents double-click during swap

  const loadData = async () => {
    try {
      const res = await fetchAdminCategories();
      setList(res.data || []);
    } catch (err) {
      console.error("Error loading categories", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ─── REORDER ─────────────────────────────────────────────────────────────
  // Swap the sortOrder of the category at `index` with the one above it.
  const handleMoveUp = async (index) => {
    if (index === 0 || reordering) return;
    setReordering(true);
    try {
      await swapCategorySortOrder(list[index], list[index - 1]);
      await loadData();
    } catch (err) {
      console.error("Reorder error", err);
    } finally {
      setReordering(false);
    }
  };

  // Swap the sortOrder of the category at `index` with the one below it.
  const handleMoveDown = async (index) => {
    if (index === list.length - 1 || reordering) return;
    setReordering(true);
    try {
      await swapCategorySortOrder(list[index], list[index + 1]);
      await loadData();
    } catch (err) {
      console.error("Reorder error", err);
    } finally {
      setReordering(false);
    }
  };

  // ─── CRUD ─────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!newName.trim()) {
      setAddError("Category name is required");
      return;
    }
    try {
      await createAdminCategory(newName.trim());
      setNewName("");
      setAdding(false);
      setAddError("");
      loadData();
    } catch (err) {
      setAddError(err?.response?.data?.msg || "Error adding category");
    }
  };

  const handleUpdate = async () => {
    try {
      await updateAdminCategory(editing._id, {
        name: editing.name,
        active: editing.active
      });
      setEditing(null);
      loadData();
    } catch (err) {
      console.error("Update error", err);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteAdminCategory(deleteId);
    setDeleteId(null);
    loadData();
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2>Categories</h2>
        <button
          className="btn save"
          onClick={() => { setAdding(true); setAddError(""); setNewName(""); }}
        >
          + Add Category
        </button>
      </div>

      {/* Category list */}
      {list.length === 0 ? (
        <p>No categories found</p>
      ) : (
        <div className="category-order-list">
          {list.map((cat, index) => (
            <div key={cat._id} className="category-order-item">

              {/* Position badge */}
              <span className="category-order-badge">{index + 1}</span>

              {/* Name + status (desktop layout — stacked column) */}
              <div className="category-order-info">
                <span className="category-order-name">{cat.name}</span>
                <span className={cat.active ? "status-active" : "status-inactive"}>
                  {cat.active ? "Active" : "Inactive"}
                </span>
              </div>

              {/* Actions */}
              <div className="category-order-actions">
                {/* Status badge shown only on mobile (left of row 2) */}
                <span className={`cat-status-mobile ${cat.active ? "status-active" : "status-inactive"}`}>
                  {cat.active ? "Active" : "Inactive"}
                </span>
                {/* Up / Down arrows */}
                <button
                  className="order-btn"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0 || reordering}
                  title="Move up"
                  aria-label={`Move ${cat.name} up`}
                >
                  ▲
                </button>
                <button
                  className="order-btn"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === list.length - 1 || reordering}
                  title="Move down"
                  aria-label={`Move ${cat.name} down`}
                >
                  ▼
                </button>

                {/* Edit / Delete */}
                <button className="edit"  onClick={() => setEditing({ ...cat })}>Edit</button>
                <button className="delete" onClick={() => setDeleteId(cat._id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── ADD MODAL ─────────────────────────────────────────────────── */}
      {adding && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Add Category</h3>

            <input
              type="text"
              placeholder="Category name"
              value={newName}
              onChange={(e) => { setNewName(e.target.value); setAddError(""); }}
              className="modal-input"
            />

            {addError && <p className="modal-error">{addError}</p>}

            <div className="modal-actions">
              <button
                className="btn cancel"
                onClick={() => { setAdding(false); setNewName(""); setAddError(""); }}
              >
                Cancel
              </button>
              <button className="btn save" onClick={handleAdd}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ────────────────────────────────────────────────── */}
      {editing && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Edit Category</h3>

            <input
              type="text"
              placeholder="Category name"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              className="modal-input"
            />

            <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "10px 0" }}>
              <input
                type="checkbox"
                id="activeToggle"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              <label htmlFor="activeToggle">Active (visible to users)</label>
            </div>

            <div className="modal-actions">
              <button className="btn cancel" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn save"   onClick={handleUpdate}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ──────────────────────────────────────── */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal-box small">
            <h3>Delete Category?</h3>
            <p>Existing businesses with this category will still appear in search results.</p>

            <div className="modal-actions">
              <button className="btn cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn delete" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Categories;
